// SAVED GUIDES — agent-generated spotlight tours, stored as `type: 'guide'`
// entries in the PROJECT'S FORK VAULT (body = the tour JSON, schema of
// TutorialFlow). They are part of the project/app they explain: the fork
// carries them, the Docs store lists them, and here they are fetched and
// registered into the tutorial registry at runtime so the Tutorial chooser
// and the chat rail launch them like any built-in tour.
//
// Read path: the existing docs-engine proxy (POST /api/docs/engine — the
// allowlisted hop to the fork MCP vault). No new backend surface.
import { fetchData } from "../../fetchapi/fetchapi";
import { registerProjectTours } from "./tours/index";

// slug -> [guide tours] already registered (avoid duplicate registration —
// registerProjectTours appends).
const loadedFor = new Map();

function parseGuideEntry(entry) {
  try {
    const body = String(entry?.body || "");
    // The body is the tour JSON (optionally inside a ```json fence).
    const m = body.match(/```json\s*([\s\S]*?)```/);
    const tour = JSON.parse(m ? m[1] : body);
    if (!tour || !Array.isArray(tour.steps) || tour.steps.length === 0) return null;
    tour.key = tour.key || `guide:${entry.slug}`;
    tour.title = tour.title || entry.title || entry.slug;
    tour.__guide = { slug: entry.slug, app: tour.app || null, updated: entry.updated || entry.created || null };
    return tour;
  } catch {
    return null;
  }
}

async function engine(action, params) {
  const r = await fetchData("docs/engine", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, params: params || {} }),
  });
  return r?.data ?? r;
}

// List the project's guide entries (metadata only — bodies fetched on load).
export async function listGuides() {
  const r = await engine("list", { type: "guide" });
  const entries = !r ? [] : Array.isArray(r) ? r : (Array.isArray(r.entries) ? r.entries : (Array.isArray(r.result) ? r.result : []));
  // Saved guides own the guide-* slug namespace (the delete allowlist
  // enforces the same rule); other type:guide entries (e.g. static harness
  // notes) are not launchable tours.
  return (Array.isArray(entries) ? entries : []).filter((e) => e && typeof e.slug === "string" && e.slug.startsWith("guide-"));
}

// Fetch + parse + register every guide for the active project. Idempotent per
// slug set; returns the registered tours.
export async function loadProjectGuides(projectSlug) {
  if (!projectSlug) return [];
  const entries = await listGuides().catch(() => []);
  const sig = entries.map((e) => e.slug).sort().join("|");
  const prev = loadedFor.get(projectSlug);
  if (prev && prev.sig === sig) return prev.tours;
  const tours = [];
  for (const e of entries) {
    try {
      const full = await engine("get", { slug: e.slug });
      const tour = parseGuideEntry(full?.entry || full);
      if (tour) tours.push(tour);
    } catch { /* skip unparseable guides */ }
  }
  loadedFor.set(projectSlug, { sig, tours });
  registerProjectTours(projectSlug, tours, { replaceGuides: true });
  return tours;
}

// Launch one guide by vault slug: make sure it is registered, then fire the
// tutorial engine. Returns true when the tour was found.
export async function launchGuide(projectSlug, guideSlug) {
  const tours = await loadProjectGuides(projectSlug);
  const tour = tours.find((t) => t.__guide?.slug === guideSlug);
  if (!tour) return false;
  window.dispatchEvent(new CustomEvent("cortex:start-tutorial", { detail: { tour: tour.key } }));
  return true;
}

// Delete one saved guide (the docs-engine allowlist only permits guide-*
// slugs) and drop the per-project cache so the next load re-lists.
export async function deleteGuide(projectSlug, guideSlug) {
  await engine("delete", { slug: guideSlug });
  if (projectSlug) loadedFor.delete(projectSlug);
}

// Force the next loadProjectGuides() to re-list (e.g. after an external
// build completes) without waiting for the slug-set signature to change.
export function invalidateGuides(projectSlug) {
  if (projectSlug) loadedFor.delete(projectSlug);
}

// ── PDFs ─────────────────────────────────────────────────────────────────────
// Each saved guide can carry ONE rendered PDF — cover + every step's text + a
// screenshot of the screen it spotlights — produced by shared/guide-pdf/render.mjs
// in a real browser when the guide was built (the fleet runs it right after
// saving) and stored in cortex (modules/guides). The UI only lists and
// downloads; it never renders (it cannot photograph the app embed iframe).

// slug -> { title, steps, pages, bytes, guide_updated, created_at, created_by }
export async function listGuidePdfs() {
  const r = await fetchData("guides/pdf");
  const out = new Map();
  for (const p of (r?.pdfs || [])) if (p?.guide_slug) out.set(p.guide_slug, p);
  return out;
}

// Fetch the stored PDF with the session bearer (a plain <a href> would arrive
// unauthenticated) and hand it to the browser as a download.
export async function downloadGuidePdf(guideSlug) {
  const blob = await fetchData(`guides/${encodeURIComponent(guideSlug)}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${guideSlug}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return blob.size;
}
